import { useEffect, useRef, useState, CSSProperties } from 'react';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';

interface TouchKeyboardProps {
  /** Current text; the keyboard's internal buffer is kept in sync with this. */
  value: string;
  /** Called with the full updated string on every key. */
  onChange: (value: string) => void;
  /** Greys out and blocks input (e.g. while the parent is submitting). */
  disabled?: boolean;
  /** Renders a "Hide keyboard" affordance when provided. */
  onHide?: () => void;
}

/**
 * On-screen QWERTY keyboard for touch-screen operator stations, where there is
 * no physical keyboard. Controlled: the parent owns the text, this keeps the
 * react-simple-keyboard buffer in sync so physical typing and the on-screen
 * keys stay consistent. Shift auto-releases after one key (phone behaviour).
 */
const LAYOUT = {
  default: [
    '1 2 3 4 5 6 7 8 9 0',
    'q w e r t y u i o p',
    'a s d f g h j k l',
    '{shift} z x c v b n m {bksp}',
    ", . ' - {space} {enter}",
  ],
  shift: [
    '! ? # $ % & * ( ) /',
    'Q W E R T Y U I O P',
    'A S D F G H J K L',
    '{shift} Z X C V B N M {bksp}',
    ': ; " @ {space} {enter}',
  ],
};

const DISPLAY = {
  '{bksp}': '⌫',
  '{shift}': '⇧',
  '{space}': 'space',
  '{enter}': '⏎',
};

export default function TouchKeyboard({ value, onChange, disabled, onHide }: TouchKeyboardProps) {
  const keyboard = useRef<any>(null);
  const [layoutName, setLayoutName] = useState('default');

  // Keep the keyboard's internal buffer aligned with the parent's value when it
  // changes from outside the keyboard (reset on close, physical typing, etc.).
  useEffect(() => {
    if (keyboard.current && keyboard.current.getInput() !== value) {
      keyboard.current.setInput(value);
    }
  }, [value]);

  const handleKeyPress = (button: string) => {
    if (button === '{shift}' || button === '{lock}') {
      setLayoutName((n) => (n === 'default' ? 'shift' : 'default'));
      return;
    }
    // Drop back to lowercase after one shifted key, like a phone keyboard.
    if (layoutName === 'shift') setLayoutName('default');
  };

  const wrapStyle: CSSProperties = {
    marginTop: 12,
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
  };

  return (
    <div style={wrapStyle} onClick={(e) => e.stopPropagation()}>
      {onHide && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <button
            type="button"
            className="refresh-btn"
            onClick={onHide}
            disabled={disabled}
            style={{ fontSize: 12, padding: '3px 10px' }}
          >
            Hide keyboard ⌄
          </button>
        </div>
      )}
      <div className="touch-keyboard">
        <Keyboard
          keyboardRef={(r) => (keyboard.current = r)}
          layoutName={layoutName}
          layout={LAYOUT}
          display={DISPLAY}
          onChange={onChange}
          onKeyPress={handleKeyPress}
          disableButtonHold
          mergeDisplay
        />
      </div>
    </div>
  );
}
